import { forwardRef, type JSX, type Ref } from 'react'
import {
  Content,
  type DropdownMenuContentProps,
  type DropdownMenuPortalProps,
  type DropdownMenuProps,
  type DropdownMenuTriggerProps,
  Item,
  Portal,
  Root,
  Separator,
  Trigger
} from '@radix-ui/react-dropdown-menu'
import type { TooltipProviderProps } from '@radix-ui/react-tooltip'

import { TooltipProvider } from '../Tooltip'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'
import { type DropdownItemsProps, type RadixDropdownItemProps, type RadixDropdownSeparatorProps } from './types'

export type Props = DropdownMenuProps & {
  disabled?: boolean
  items: DropdownItemsProps[]
  triggerProps?: DropdownMenuTriggerProps
  portalProps?: DropdownMenuPortalProps
  contentProps?: DropdownMenuContentProps
  closeOnSelect?: boolean
  useTooltipProvider?: boolean
  tooltipProviderProps?: Omit<TooltipProviderProps, 'children'>
  side?: 'top' | 'right' | 'bottom' | 'left' | undefined
}

const Dropdown = forwardRef(
  (
    {
      children,
      disabled,
      items = [],
      triggerProps,
      portalProps,
      contentProps,
      closeOnSelect = true,
      useTooltipProvider = true,
      tooltipProviderProps,
      side = 'top',
      ...restProps
    }: Props,
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    const renderItem = (index: number, { component, ...itemProps }: RadixDropdownItemProps): JSX.Element => (
      <Item
        key={index}
        {...itemProps}
        onSelect={(event) => {
          if (!closeOnSelect) {
            event.preventDefault()
          }

          // Cast the event to any to bypass type checking
          itemProps.onSelect?.(event as any)
        }}
      >
        {component}
      </Item>
    )

    const renderSeparator = (index: number, itemProps: RadixDropdownSeparatorProps): JSX.Element => (
      <Separator
        key={index}
        {...itemProps}
        className={resolveTailwindClasses('my-px h-px bg-primary-900', itemProps.className)}
      />
    )

    const renderContent = (
      index: number,
      { type, ...itemProps }: RadixDropdownItemProps | RadixDropdownSeparatorProps
    ): JSX.Element => {
      if (type === 'separator') {
        return renderSeparator(index, itemProps as RadixDropdownSeparatorProps)
      }

      return renderItem(index, itemProps as RadixDropdownItemProps)
    }

    return (
      <Root {...restProps}>
        <Trigger
          asChild
          onClick={(e) => (triggerProps?.onClick ? triggerProps.onClick(e) : e.stopPropagation())}
          disabled={disabled}
          {...(triggerProps || {})}
        >
          {children}
        </Trigger>

        <Portal {...(portalProps || {})}>
          <Content
            ref={ref}
            loop
            collisionPadding={8}
            side={side}
            align="start"
            {...(contentProps || {})}
            className={resolveTailwindClasses(
              'z-50 mt-1 max-h-[var(--radix-popper-available-height)] min-w-fit space-y-px overflow-y-auto rounded-lg bg-primary-1000 p-0 antialiased shadow-dropdown',
              contentProps?.className
            )}
          >
            {useTooltipProvider ? (
              <TooltipProvider delayDuration={140} disableHoverableContent {...(tooltipProviderProps || {})}>
                {items.map((item, index) => renderContent(index, item))}
              </TooltipProvider>
            ) : (
              items.map((item, index) => renderContent(index, item))
            )}
          </Content>
        </Portal>
      </Root>
    )
  }
)

Dropdown.displayName = 'Dropdown'

export default Dropdown
